import {
  FC,
  createContext,
  useContext,
  useEffect,
  useState,
  useRef
} from 'react'
import { NextRouter, useRouter } from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import { convertQueryParamsAndRemoveEmptyGetParameters } from '@/core/toStringHelper'
import { useMhub } from 'providers/mhubProvider'
import { FilterQueryItem } from 'types/filterQueryItem'
import {
  ActiveFilterItem,
  ActiveFiltersPriceType
} from 'types/activeFiltersType'
import { parseQueryFromActiveFilters } from '@/utils/convertActiveFiltersToQuery'
import {
  ActiveFiltersContextType,
  ActiveFiltersDispatchContextType,
  ActiveFiltersTypeAliasContext
} from './filterProviderTypes'
import { Filter, FilterItem } from '@/network/graphql.g'
import {
  parseFilterQueryIdsFromQuery,
  parseValidFilters,
  restoreMissingSelectedFilters
} from '@/providers/filterProvider/parsingFilters'
import {
  convertAPIFilterToFilterType,
  FilterModel
} from '../../types/filterModel'

const ActiveFiltersContext = createContext<ActiveFiltersContextType>(null)

const ActiveFiltersContextDispatch =
  createContext<ActiveFiltersDispatchContextType>(null)

export const useFilter = (): ActiveFiltersContextType => {
  return useContext(ActiveFiltersContext)
}

export const useActiveFilterDispatch = (): ActiveFiltersDispatchContextType =>
  useContext(ActiveFiltersContextDispatch)

export const useActiveFiltersCount = (): number => {
  const context = useContext(ActiveFiltersContext)
  const activeFilters = context.activeFilters
  if (!activeFilters) {
    return 0
  }
  return (
    (activeFilters.price.query != null ? 1 : 0) +
    (activeFilters.manufacturers?.length || 0) +
    (activeFilters.colors?.length || 0) +
    (activeFilters.patterns?.length || 0) +
    (activeFilters.materials?.length || 0) +
    (activeFilters.inStock ? 1 : 0) +
    (activeFilters.inMainWarehouse ? 1 : 0) +
    (activeFilters.inSale ? 1 : 0) +
    (activeFilters.sizes?.length || 0) +
    (activeFilters.tags?.reduce(
      (prev, current) => prev + current.items.length,
      0
    ) || 0) +
    (activeFilters.genders?.length || 0) +
    (activeFilters.categories?.length || 0) +
    (activeFilters.news != null ? 1 : 0)
  )
}

export const useFilterCountByType = (
  filterType: FilterQueryItem,
  parentId?: string
): number => {
  const context = useContext(ActiveFiltersContext)
  const activeFilters = context.activeFilters
  if (!activeFilters) {
    return 0
  }

  switch (filterType) {
    case FilterQueryItem.COLOR:
      return activeFilters.colors?.length || 0
    case FilterQueryItem.SIZE:
      return activeFilters.sizes?.length || 0
    case FilterQueryItem.MANUFACTURER:
      return activeFilters.manufacturers?.length || 0
    case FilterQueryItem.MATERIAL:
      return activeFilters.materials?.length || 0
    case FilterQueryItem.PATTERN:
      return activeFilters.patterns?.length || 0
    case FilterQueryItem.GENDER:
      return activeFilters.genders?.length || 0
    case FilterQueryItem.CATEGORY:
      return activeFilters.categories?.length || 0
    case FilterQueryItem.TAG:
      return (
        activeFilters.tags?.find((tag) => tag.id === parentId)?.items?.length ||
        0
      )
    case FilterQueryItem.PRICE_MIN:
    case FilterQueryItem.PRICE_MAX:
      return activeFilters.price?.query != null ? 1 : 0
    default:
      return 0
  }
}

type FilterProviderProps = {
  filter?: Filter[]
}

type FilterProviderStateType = {
  filter?: FilterModel
  activeFilters?: ActiveFiltersTypeAliasContext
  manufacturerActiveFilterIds?: string[]
  colorActiveFilterIds?: string[]
  materialActiveFilterIds?: string[]
  sizeActiveFilterIds?: string[]
  genderActiveFilterIds?: string[]
  categoryActiveFilterIds?: string[]
  tagActiveFilterIds?: { id: string; items: string[] }[]
  patternActiveFilterIds?: string[]
}

const FiltersProvider: FC<FilterProviderProps> = ({ filter, children }) => {
  const router = useRouter()

  const definitionsCache = useRef<Record<string, Record<string, FilterItem>>>(
    {}
  )

  const updateCache = (model: FilterModel) => {
    const cacheSection = (key: keyof FilterModel, items: any[]) => {
      if (!items) {
        return
      }
      if (!definitionsCache.current[key]) {
        definitionsCache.current[key] = {}
      }

      items.forEach((section) => {
        if (section.items) {
          section.items.forEach((item: FilterItem) => {
            definitionsCache.current[key][item.id] = item
          })
        }
      })
    }

    cacheSection('manufacturers', model.manufacturers)
    cacheSection('colors', model.colors)
    cacheSection('patterns', model.patterns)
    cacheSection('materials', model.materials)
    cacheSection('sizes', model.sizes)
    cacheSection('genders', model.genders)
    cacheSection('categories', model.categories)

    if (model.tags) {
      model.tags.forEach((tagSection) => {
        const key = `tag_${tagSection.id}`
        if (!definitionsCache.current[key]) {
          definitionsCache.current[key] = {}
        }
        tagSection.items?.forEach((item) => {
          definitionsCache.current[key][item.id] = item
        })
      })
    }
  }

  const makeStateObject = (filter: Filter[]): FilterProviderStateType => {
    const query = parseFilterQueryIdsFromQuery(router.query)
    const filterModel = convertAPIFilterToFilterType(filter)

    updateCache(filterModel)

    restoreMissingSelectedFilters(filterModel, query, definitionsCache.current)

    const activeFilters = parseValidFilters(query, filterModel)

    return {
      filter: filterModel,
      activeFilters: activeFilters,
      manufacturerActiveFilterIds: activeFilters.manufacturers?.map(
        ({ id }) => id
      ),
      colorActiveFilterIds: activeFilters.colors?.map(({ id }) => id) ?? [],
      patternActiveFilterIds: activeFilters.patterns?.map(({ id }) => id) ?? [],
      materialActiveFilterIds:
        activeFilters.materials?.map(({ id }) => id) ?? [],
      sizeActiveFilterIds: activeFilters.sizes?.map(({ id }) => id) ?? [],
      genderActiveFilterIds: activeFilters.genders?.map(({ id }) => id) ?? [],
      categoryActiveFilterIds:
        activeFilters.categories?.map(({ id }) => id) ?? [],
      tagActiveFilterIds:
        activeFilters.tags?.map(({ id, items }) => ({
          id,
          items: items.map(({ id }) => id)
        })) ?? []
    }
  }

  useEffect(() => {
    setActiveFilters(makeStateObject(filter))
  }, [filter, router.query])

  const [activeFilters, setActiveFilters] = useState<FilterProviderStateType>(
    makeStateObject(filter)
  )

  const {
    pushSortingFilterAdd,
    pushPriceFilterAdd,
    pushPriceFilterRemove,
    pushManufacturerFilterAdd,
    pushManufacturerFilterRemove,
    pushColorFilterAdd,
    pushColorFilterRemove,
    pushMotiveFilterAdd,
    pushMotiveFilterRemove,
    pushMaterialFilterAdd,
    pushMaterialFilterRemove,
    pushSizeFilterAdd,
    pushSizeFilterRemove,
    pushGenderFilterAdd,
    pushGenderFilterRemove,
    pushCategoryFilterAdd,
    pushCategoryFilterRemove,
    pushTagFilterAdd,
    pushTagFilterRemove,
    pushInStockFilterAdd,
    pushInStockFilterRemove,
    pushInMainWarehouseFilterAdd,
    pushInMainWarehouseFilterRemove,
    pushInSaleFilterAdd,
    pushInSaleFilterRemove,
    pushNewProductsFilterAdd,
    pushNewProductsFilterRemove
  } = useMhub()

  const toggleFilterAndSetUrl = (
    id: string | boolean | ActiveFiltersPriceType,
    name: string,
    getParamType: FilterQueryItem,
    parentId?: string,
    parentName?: string
  ) => {
    const isEmpty = (value: number): boolean =>
      value === null || value === undefined || isNaN(value)
    const defaultIfEmpty = (value: number, defaultValue: number): number =>
      !isEmpty(value) ? value : defaultValue

    setTimeout(function () {
      const localActiveFilters = { ...activeFilters.activeFilters }

      switch (getParamType) {
        case FilterQueryItem.COLOR:
          if (activeFilters.colorActiveFilterIds?.includes(id as string)) {
            pushColorFilterRemove(id as string, name)
          } else {
            pushColorFilterAdd(id as string, name)
          }
          localActiveFilters.colors = modifyChangeItem(
            localActiveFilters.colors,
            id as string,
            activeFilters.colorActiveFilterIds
          )
          break
        case FilterQueryItem.PATTERN:
          if (activeFilters.patternActiveFilterIds?.includes(id as string)) {
            pushMotiveFilterRemove(id as string, name)
          } else {
            pushMotiveFilterAdd(id as string, name)
          }
          localActiveFilters.patterns = modifyChangeItem(
            localActiveFilters.patterns,
            id as string,
            activeFilters.patternActiveFilterIds
          )
          break
        case FilterQueryItem.MATERIAL:
          if (activeFilters.materialActiveFilterIds?.includes(id as string)) {
            pushMaterialFilterRemove(id as string, name)
          } else {
            pushMaterialFilterAdd(id as string, name)
          }
          localActiveFilters.materials = modifyChangeItem(
            localActiveFilters.materials,
            id as string,
            activeFilters.materialActiveFilterIds
          )
          break
        case FilterQueryItem.MANUFACTURER:
          if (
            activeFilters.manufacturerActiveFilterIds?.includes(id as string)
          ) {
            pushManufacturerFilterRemove(id as string, name)
          } else {
            pushManufacturerFilterAdd(id as string, name)
          }
          localActiveFilters.manufacturers = modifyChangeItem(
            localActiveFilters.manufacturers,
            id as string,
            activeFilters.manufacturerActiveFilterIds
          )
          break
        case FilterQueryItem.SIZE:
          if (activeFilters.sizeActiveFilterIds?.includes(id as string)) {
            pushSizeFilterRemove(id as string, name)
          } else {
            pushSizeFilterAdd(id as string, name)
          }
          localActiveFilters.sizes = modifyChangeItem(
            localActiveFilters.sizes,
            id as string,
            activeFilters.sizeActiveFilterIds
          )
          break
        case FilterQueryItem.GENDER:
          if (activeFilters.genderActiveFilterIds?.includes(id as string)) {
            pushGenderFilterRemove(id as string, name)
          } else {
            pushGenderFilterAdd(id as string, name)
          }
          localActiveFilters.genders = modifyChangeItem(
            localActiveFilters.genders,
            id as string,
            activeFilters.genderActiveFilterIds
          )
          break
        case FilterQueryItem.CATEGORY:
          if (activeFilters.categoryActiveFilterIds?.includes(id as string)) {
            pushCategoryFilterRemove(id as string, name)
          } else {
            pushCategoryFilterAdd(id as string, name)
          }
          localActiveFilters.categories = modifyChangeItem(
            localActiveFilters.categories,
            id as string,
            activeFilters.categoryActiveFilterIds
          )

          break
        case FilterQueryItem.CATEGORY_CAMPAIGN:
          localActiveFilters.categories = [
            { id: id as string, title: id as string, englishName: id as string }
          ]
          break

        case FilterQueryItem.TAG:
          // eslint-disable-next-line no-case-declarations
          const activeFilterIds = activeFilters.tagActiveFilterIds?.find(
            (tag) => tag.id === parentId
          )?.items

          if (activeFilterIds?.includes(id as string)) {
            pushTagFilterRemove(parentId, parentName, id as string, name)
          } else {
            pushTagFilterAdd(parentId, parentName, id as string, name)
          }

          // eslint-disable-next-line no-case-declarations
          const currentTag = localActiveFilters.tags?.find(
            (tag) => tag.id === parentId
          )
          if (currentTag) {
            currentTag.items = modifyChangeItem(
              currentTag.items,
              id as string,
              activeFilterIds
            )
          }
          break
        case FilterQueryItem.STOCK:
          if (localActiveFilters.inStock) {
            localActiveFilters.inStock = null
            pushInStockFilterRemove(undefined)
          } else {
            localActiveFilters.inStock = true
            pushInStockFilterAdd(undefined)
          }
          break
        case FilterQueryItem.MAIN_WAREHOUSE:
          if (localActiveFilters.inMainWarehouse) {
            localActiveFilters.inMainWarehouse = null
            pushInMainWarehouseFilterRemove(undefined)
          } else {
            localActiveFilters.inMainWarehouse = true
            pushInMainWarehouseFilterAdd(undefined)
          }
          break
        case FilterQueryItem.IN_SALE:
          if (localActiveFilters.inSale) {
            localActiveFilters.inSale = null
            pushInSaleFilterRemove(undefined)
          } else {
            localActiveFilters.inSale = true
            pushInSaleFilterAdd(undefined)
          }
          break
        case FilterQueryItem.NEWS:
          if (localActiveFilters.news) {
            localActiveFilters.news = null
            pushNewProductsFilterRemove(undefined)
          } else {
            localActiveFilters.news = true
            pushNewProductsFilterAdd(undefined)
          }
          break
        case FilterQueryItem.SORT:
          localActiveFilters.sort = {
            id: id as string,
            title: id as string
          }
          pushSortingFilterAdd(localActiveFilters.sort?.id)
          break
        case FilterQueryItem.PRICE_MIN:
        case FilterQueryItem.PRICE_MAX:
          if (
            id === null ||
            (isEmpty((id as ActiveFiltersPriceType).min) &&
              !isEmpty(localActiveFilters.price?.query?.min)) ||
            (isEmpty((id as ActiveFiltersPriceType).max) &&
              !isEmpty(localActiveFilters.price?.query?.max))
          ) {
            pushPriceFilterRemove({
              min: defaultIfEmpty(
                (id as ActiveFiltersPriceType)?.min,
                localActiveFilters.price?.original?.min
              ),
              max: defaultIfEmpty(
                (id as ActiveFiltersPriceType)?.max,
                localActiveFilters.price?.original?.max
              )
            })
          } else {
            pushPriceFilterAdd({
              min: defaultIfEmpty(
                (id as ActiveFiltersPriceType).min,
                localActiveFilters.price?.original?.min
              ),
              max: defaultIfEmpty(
                (id as ActiveFiltersPriceType).max,
                localActiveFilters.price?.original?.max
              )
            })
          }
          localActiveFilters.price = { query: id as ActiveFiltersPriceType }
          break
      }
      pushNewActiveFilter(
        parseQueryFromActiveFilters(router.query, localActiveFilters),
        router
      )
    }, 0)
  }

  const discardAllFilter = (getParamType?: FilterQueryItem | string) => {
    let query = parseQueryFromActiveFilters(
      router.query,
      activeFilters.activeFilters
    )
    if (getParamType) {
      query[getParamType] = null
    } else {
      // do not remove search
      if (query[FilterQueryItem.SEARCH]) {
        query = {
          [FilterQueryItem.SEARCH]: query[FilterQueryItem.SEARCH]
        }
      } else {
        query = {}
      }
    }
    pushNewActiveFilter(query, router)
  }

  const namesForActiveFilterItem = (items: ActiveFilterItem[]): string[] => {
    return items?.map(({ title }) => title) ?? []
  }

  const isInColorFilters = (id: string): boolean =>
    activeFilters.colorActiveFilterIds?.includes(id)

  const isInPatternFilters = (id: string): boolean =>
    activeFilters.patternActiveFilterIds?.includes(id)

  const isInMaterialFilters = (id: string): boolean =>
    activeFilters.materialActiveFilterIds?.includes(id)

  const isInManufacturerFilters = (id: string): boolean =>
    activeFilters.manufacturerActiveFilterIds?.includes(id)

  const isInSizeFilters = (id: string): boolean =>
    activeFilters.sizeActiveFilterIds?.includes(id)

  const isInGenderFilters = (id: string): boolean =>
    activeFilters.genderActiveFilterIds?.includes(id)

  const isInCategoryFilters = (id: string): boolean =>
    activeFilters.categoryActiveFilterIds?.includes(id)

  const isInTagFilters = (tagId: string, id: string): boolean =>
    activeFilters.tagActiveFilterIds
      ?.find((tag) => tag.id === tagId)
      ?.items?.includes(id)

  const modifyChangeItem = (
    items: ActiveFilterItem[],
    itemId: string,
    allActiveFilterIds: string[]
  ): ActiveFilterItem[] => {
    return allActiveFilterIds?.includes(itemId)
      ? (items ?? []).filter(({ id }) => id !== itemId)
      : [...(items ?? []), { id: itemId, title: itemId, englishName: itemId }]
  }

  const setFilters = (filter) => setActiveFilters(makeStateObject(filter))

  const setFiltersForType = (
    getParamType: FilterQueryItem,
    ids: string[],
    parentId?: string,
    parentName?: string
  ) => {
    const localActiveFilters = { ...activeFilters.activeFilters }

    switch (getParamType) {
      case FilterQueryItem.COLOR:
        localActiveFilters.colors = ids.map((id) => ({
          id,
          title: id,
          englishName: id
        }))
        break
      case FilterQueryItem.SIZE:
        localActiveFilters.sizes = ids.map((id) => ({
          id,
          title: id,
          englishName: id
        }))
        break
      case FilterQueryItem.PATTERN:
        localActiveFilters.patterns = ids.map((id) => ({
          id,
          title: id,
          englishName: id
        }))
        break
      case FilterQueryItem.MATERIAL:
        localActiveFilters.materials = ids.map((id) => ({
          id,
          title: id,
          englishName: id
        }))
        break
      case FilterQueryItem.MANUFACTURER:
        localActiveFilters.manufacturers = ids.map((id) => ({
          id,
          title: id,
          englishName: id
        }))
        break
      case FilterQueryItem.GENDER:
        localActiveFilters.genders = ids.map((id) => ({
          id,
          title: id,
          englishName: id
        }))
        break
      case FilterQueryItem.CATEGORY:
        localActiveFilters.categories = ids.map((id) => ({
          id,
          title: id,
          englishName: id
        }))
        break
      case FilterQueryItem.TAG:
        localActiveFilters.tags = [
          ...(localActiveFilters.tags?.filter((t) => t.id !== parentId) ?? []),
          {
            id: parentId,
            title: parentName ?? parentId,
            items: ids.map((id) => ({ id, title: id, englishName: id }))
          }
        ]
        break
    }

    pushNewActiveFilter(
      parseQueryFromActiveFilters(router.query, localActiveFilters),
      router
    )
  }

  return (
    <ActiveFiltersContextDispatch.Provider
      value={{
        setFilters,
        toggleFilterAndSetUrl,
        setFiltersForType,
        namesForActiveFilterItem,
        discardAllFilter,
        isInColorFilters,
        isInPatternFilters,
        isInMaterialFilters,
        isInManufacturerFilters,
        isInSizeFilters,
        isInGenderFilters,
        isInCategoryFilters,
        isInTagFilters
      }}
    >
      <ActiveFiltersContext.Provider
        value={{
          activeFilters: activeFilters.activeFilters,
          filter: activeFilters.filter
        }}
      >
        {children}
      </ActiveFiltersContext.Provider>
    </ActiveFiltersContextDispatch.Provider>
  )
}

export default FiltersProvider

const pushNewActiveFilter = (query: ParsedUrlQuery, router: NextRouter) => {
  query.page = null
  const stringQuery = convertQueryParamsAndRemoveEmptyGetParameters({
    query,
    allowOnly: Object.values(FilterQueryItem)
  })
  const basePath = router.asPath.split('?')[0]
  const path = `${basePath}${stringQuery ? '?' : ''}${stringQuery}`

  routerPushShallow(path, router, query)
}

export const routerPushShallow = (
  as: string,
  router: NextRouter,
  query: ParsedUrlQuery
): void => {
  const url = getShallowUrl(router, query)
  router.push(url, as, {
    shallow: true
  })
}

const getShallowUrl = (router: NextRouter, query: ParsedUrlQuery): string => {
  const routeStringQuery = convertQueryParamsAndRemoveEmptyGetParameters({
    query
  })
  return `${router.route}${routeStringQuery ? '?' : ''}${routeStringQuery}`
}
